import React from 'react'

import { Typography } from '@mui/material'

import { ConfirmSettingModal } from '../ConfirmSettingModal'
import type { ConvertDemoCertificateRefusal } from 'services'
import { getErrorMessage } from 'utils'

/**
 * The confirmation gate for the two VRG-836 `demo` row conversions.
 *
 * ## Why this modal exists at all — the permission does not say what the action costs
 *
 * Both conversions are gated on `sample_data_manage`, whose own catalog description is
 * "add or clear the labelled sample products, certificates and scans used to demonstrate an
 * empty console. **Samples are never billed.**" The certificate convert makes that last
 * sentence false for one row: it clears `example_kind` AND calls `recordActivationUsage`,
 * writing a `certificate_metering` row that lands on the brand's next invoice.
 *
 * So an admin holding a permission described as sample-data housekeeping can spend money,
 * and nothing in the permission's wording tells them. Minting a second permission was
 * considered and REJECTED (product decision, recorded in `demo-data.controller.ts`):
 * requiring two would 403 any admin holding only one. The decision taken instead is that
 * **the confirm names the cost** — which is what `ConfirmSettingModal` is for (§43: a Save
 * button gates a change without ever saying what it costs; a consequence says it).
 *
 * ## Why ONE component with a `kind`, and why the copy is NOT shared
 *
 * One component because the two gates are structurally identical — state the consequence,
 * then ask — and a second file would be the hand-roll §36 says to extract.
 *
 * The COPY is per-kind and must stay that way. The stakes genuinely differ:
 *
 *   PRODUCT      a claim about TRUTH. Vera guessed this product's title, copy and images
 *                from the brand's public website; converting says the guess is right and
 *                the brand owns the claim. Free. Forward-only, and it deliberately does NOT
 *                reach certificates already minted off it — they stay demo and stay free,
 *                which is why `demoCertificatesRetained` is in the copy rather than left
 *                for the brand to discover on their next invoice.
 *   CERTIFICATE  a decision to BE BILLED. Metered today, in the current period, never
 *                backdated.
 *
 * A single sentence covering both would either overstate the product convert (scaring a
 * brand off a free, accurate claim) or understate the certificate one (the failure this
 * whole ticket exists to prevent). §15: separate sentences, not em-dash-joined clauses.
 *
 * ## Mechanics the reader should not have to infer
 *
 * - GATED BEFORE APPLYING. Nothing is called until the user confirms; the caller stages the
 *   pending row and this modal is what fires the mutation. There is no optimistic flip to
 *   revert, so a cancel cannot leave the UI having lied about what is stored.
 * - NOT `destructive`. Converting creates nothing and destroys nothing — it ends a
 *   classification (§32: the red treatment is for a record ceasing to exist). The warning
 *   `Alert` that `ConfirmSettingModal` always renders carries the weight.
 * - The modal stays OPEN on failure, with the error inline, because closing it would read
 *   as success. Same contract as the "Remove sample" confirm.
 */

export type ConvertDemoRowKind = 'product' | 'certificate'

/**
 * What to tell the user when the server REFUSED a certificate conversion.
 *
 * Lives here, exported, rather than inline at each call site: the certificates list and the
 * certificate detail page both fire this mutation, and two hand-written copies of the same
 * four-branch message is exactly the drift §36 extracts on the second hand-roll.
 *
 * ⚠️ `not-a-demo-certificate` must NOT be rendered as "not found". The server's own docblock
 * says absent, another company's, already-real and `intro` are reported as ONE reason on
 * purpose, so the response cannot be used to probe which ids exist. "Already real or no
 * longer demo data" is the honest reading of it, and it is also the only one the user can
 * act on — reloading shows them which.
 */
export const getConvertCertificateRefusalMessage = (
  refusal: ConvertDemoCertificateRefusal | null,
): string => {
  switch (refusal) {
    case 'company-is-still-a-demo':
      // The one refusal with a real next step, and §46 requires that the resolving action be
      // reachable: taking the demo over is the Home-page prompt, not something here.
      return 'This company is still a demo. Take the demo over first, then convert.'
    case 'certificate-has-no-tag':
      return 'This certificate has no tag, so it cannot be activated or billed. Assign a tag first.'
    case 'certificate-has-no-product':
      return 'This certificate has no product, so it cannot be activated or billed.'
    default:
      return 'This certificate is already real, or is no longer demo data. Reload to see its current state.'
  }
}

interface ConvertDemoRowModalProps {
  /**
   * Which conversion this is. It selects the COPY, and the copy is the whole point of the
   * component — the two actions cost different things and must not share a sentence.
   */
  kind: ConvertDemoRowKind
  open: boolean
  onClose: () => void
  onConfirm: () => void
  loading?: boolean
  /** The row's own name, so the reader can check they staged the one they meant. */
  name?: string | null
  /**
   * PRODUCT ONLY — how many certificates exist off this product, which the conversion
   * leaves alone. Omit (or pass 0) and that sentence is dropped rather than rendering
   * "0 certificates", which reads as a fact about nothing.
   *
   * ⚠️ This is the row's TOTAL certificate count, NOT a demo-only count, and the copy is
   * worded for exactly that. The server reports the honest `demoCertificatesRetained` only
   * in its RESPONSE, which is too late for a confirm that has to state the consequence
   * BEFORE the call (§43) — and a demo product with a converted certificate hanging off it
   * would make a pre-confirm "N demo certificates" claim false. So the sentence says how
   * many certificates came from the product and that the conversion does not reach them,
   * which is true whatever each one's own label is.
   */
  certificatesCount?: number
  /** Surfaced inline, below the consequence. The modal stays open while it is set. */
  error?: unknown
}

const CONVERT_COPY: Record<
  ConvertDemoRowKind,
  { title: string; confirmLabel: string; consequence: (name?: string | null) => string }
> = {
  product: {
    title: 'Convert this product to a real one?',
    confirmLabel: 'Convert to real product',
    consequence: name =>
      `${name ? `“${name}”` : 'This product'} will stop being labelled as demo data and ` +
      'will count as one of your own products. Vera created it from your public website, ' +
      'so check the title, description and images are right before you keep it. ' +
      'Converting a product is free. This cannot be undone.',
  },
  certificate: {
    title: 'Convert this certificate to a real one?',
    confirmLabel: 'Convert to real certificate',
    consequence: name =>
      `${name ? `The certificate for “${name}”` : 'This certificate'} will stop being ` +
      'labelled as demo data and will become billable. Converting it counts as an ' +
      'activation, and that activation will appear on your next invoice. This cannot be ' +
      'undone.',
  },
}

export const ConvertDemoRowModal = ({
  kind,
  open,
  onClose,
  onConfirm,
  loading = false,
  name,
  certificatesCount,
  error,
}: ConvertDemoRowModalProps) => {
  const copy = CONVERT_COPY[kind]
  const retained = kind === 'product' ? certificatesCount ?? 0 : 0

  return (
    <ConfirmSettingModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      loading={loading}
      title={copy.title}
      /*
       * PRODUCT ONLY, and it is context rather than a second consequence, so it goes in the
       * `preamble` slot (plain prose above the alert) — `ConfirmSettingModal`'s own docblock
       * records why two stacked alerts read as two warnings of equal weight.
       *
       * It matters because the obvious assumption is wrong: converting the product does NOT
       * convert the certificates hanging off it, so a brand expecting a bill for them (or
       * expecting them to stop being labelled) would be wrong either way.
       */
      preamble={
        retained > 0
          ? `${retained} ${retained === 1 ? 'certificate was' : 'certificates were'} ` +
            'created from this product. Converting the product does not convert ' +
            `${retained === 1 ? 'it' : 'them'}. Any still labelled as demo data stay that ` +
            'way and are not billed. Convert them separately if you want to keep them.'
          : undefined
      }
      consequence={copy.consequence(name)}
      confirmLabel={copy.confirmLabel}
    >
      {Boolean(error) && (
        <Typography variant="body3" color="error.main">
          {getErrorMessage(error)}
        </Typography>
      )}
    </ConfirmSettingModal>
  )
}
